import { useMemo } from "react";

import { faBell } from "@fortawesome/pro-solid-svg-icons/faBell";
import { faDatabase } from "@fortawesome/pro-solid-svg-icons/faDatabase";
import { faGlobe } from "@fortawesome/pro-solid-svg-icons/faGlobe";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { faUser } from "@fortawesome/pro-solid-svg-icons/faUser";
import { defineMessages, IntlShape } from "react-intl";

import Feature from "@mapmycustomers/shared/enum/Feature";

import SettingPath from "@app/enum/settings/SettingPath";
import MenuItem, { MenuCategory } from "@app/types/MenuItem";
import useFeatures from "@app/util/hook/useFeatures";

const categoryMessages = defineMessages({
  data: {
    id: "settings.menu.category.data",
    defaultMessage: "Data",
    description: "Settings Menu - Data setting",
  },
  formsAndFields: {
    id: "settings.menu.category.formsAndFields",
    defaultMessage: "Forms & Fields",
    description: "Settings Menu - Forms & Fields",
  },
  notificationAndPrivacy: {
    id: "settings.menu.category.notificationAndPrivacy",
    defaultMessage: "Notifications And Privacy",
    description: "Settings Menu - Notification And Privacy setting",
  },
  organization: {
    id: "settings.menu.category.organization",
    defaultMessage: "Organization Settings",
    description: "Settings Menu - Organization setting",
  },
  personal: {
    id: "settings.menu.category.personal",
    defaultMessage: "Personal Settings",
    description: "Settings Menu - Personal setting",
  },
});

const itemMessages = defineMessages({
  activityTypes: {
    id: "settings.menu.item.activityTypes",
    defaultMessage: "Activity Types",
    description: "Settings Menu item - Activity Types",
  },
  calendarSync: {
    id: "settings.menu.item.calendarSync",
    defaultMessage: "Calendar Sync",
    description: "Settings Menu item - Calendar Sync",
  },
  customFields: {
    id: "settings.menu.item.customFields",
    defaultMessage: "Custom Fields",
    description: "Settings Menu item - Custom Fields",
  },
  emailPreferences: {
    id: "settings.menu.item.emailPreferences",
    defaultMessage: "Email Preferences",
    description: "Settings Menu item - Email Preferences",
  },
  fileTags: {
    id: "settings.menu.item.fileTags",
    defaultMessage: "File Tags",
    description: "Settings Menu item - File Tags",
  },
  formsCreateForms: {
    id: "settings.menu.item.formsLayouts",
    defaultMessage: "Layouts",
    description: "Settings Menu item - Form Layouts",
  },
  formsManageFields: {
    id: "settings.menu.item.formsManageFields",
    defaultMessage: "Manage Fields",
    description: "Settings Menu item - Manage Fields",
  },
  formsMergeDuplicates: {
    id: "settings.menu.item.formsMergeDuplicates",
    defaultMessage: "Merge Duplicates",
    description: "Settings Menu item - Merge Duplicates",
  },
  formsPinnedFields: {
    id: "settings.menu.item.formsPinnedFields",
    defaultMessage: "Pinned Fields",
    description: "Settings Menu item - Pinned Fields",
  },
  importHistory: {
    id: "settings.menu.item.importHistory",
    defaultMessage: "Import History",
    description: "Settings Menu item - Import History",
  },
  integrations: {
    id: "settings.menu.item.integrations",
    defaultMessage: "Integrations",
    description: "Settings Menu item - Integrations",
  },
  mapPreferences: {
    id: "settings.menu.item.mapPreferences",
    defaultMessage: "Map Preferences",
    description: "Settings Menu item - Map Preferences",
  },
  marketingPreferences: {
    id: "settings.menu.item.marketingPreferences",
    defaultMessage: "Marketing Preferences",
    description: "Settings Menu item - Marketing Preferences",
  },
  mergeDuplicates: {
    id: "settings.menu.item.mergeDuplicates",
    defaultMessage: "Merge Duplicates",
    description: "Settings Menu item - Merge Duplicates",
  },
  newImport: {
    id: "settings.menu.item.newImport",
    defaultMessage: "New Import",
    description: "Settings Menu item - New Import",
  },
  notifications: {
    id: "settings.menu.item.notifications",
    defaultMessage: "Notifications",
    description: "Settings Menu item - Notifications",
  },
  organizationDetails: {
    id: "settings.menu.item.organizationDetails",
    defaultMessage: "Organization Preferences",
    description: "Settings Menu item - Organization Details",
  },
  profile: {
    id: "settings.menu.item.profile",
    defaultMessage: "Profile",
    description: "Settings Menu item - Profile",
  },
  restorableRecords: {
    id: "settings.menu.item.restorableRecords",
    defaultMessage: "Restorable Records",
    description: "Settings Menu item - Restorable Records",
  },
  sandbox: {
    id: "settings.menu.item.sandbox",
    defaultMessage: "Sandbox Environments",
    description: "Settings Menu item - Sandbox Environments",
  },
  usersAndPermissions: {
    id: "settings.menu.item.usersAndPermissions",
    defaultMessage: "Users And Permissions",
    description: "Settings Menu item - Users And Permissions",
  },
});

const useMenu = (
  intl: IntlShape,
  isOwner: boolean,
  isManager: boolean,
  isReadOnlyMember: boolean,
  doesUserHaveAccessToDupeCheck: boolean,
  doesUserHaveAccessToImports: boolean
): MenuCategory[] => {
  const { hasFeature } = useFeatures();
  return useMemo(() => {
    const dataItems: MenuItem[] = [
      ...(doesUserHaveAccessToImports
        ? [
            {
              key: intl.formatMessage(itemMessages.importHistory),
              label: intl.formatMessage(itemMessages.importHistory),
              path: SettingPath.DATA_IMPORT_HISTORY,
              uid: "import-history",
            },
            {
              key: intl.formatMessage(itemMessages.newImport),
              label: intl.formatMessage(itemMessages.newImport),
              path: SettingPath.DATA_NEW_IMPORT,
              uid: "import",
            },
          ]
        : []),
      ...(isOwner
        ? [
            {
              key: intl.formatMessage(itemMessages.integrations),
              label: intl.formatMessage(itemMessages.integrations),
              path: SettingPath.ORGANIZATION_INTEGRATIONS,
              uid: "integrations",
            },
          ]
        : []),
      ...(doesUserHaveAccessToDupeCheck
        ? [
            {
              key: intl.formatMessage(itemMessages.mergeDuplicates),
              label: intl.formatMessage(itemMessages.mergeDuplicates),
              path: SettingPath.FORMS_MERGE_DUPLICATES,
              uid: "duplicates",
            },
          ]
        : []),
      {
        key: intl.formatMessage(itemMessages.restorableRecords),
        label: intl.formatMessage(itemMessages.restorableRecords),
        path: SettingPath.DATA_RESTORABLE_RECORDS,
        uid: "undelete",
      },
      ...(isOwner
        ? [
            {
              key: intl.formatMessage(itemMessages.sandbox),
              label: intl.formatMessage(itemMessages.sandbox),
              path: SettingPath.ORGANIZATION_SANDBOX,
              uid: "sandbox",
            },
          ]
        : []),
    ];
    return [
      {
        icon: faUser,
        items: [
          ...(isReadOnlyMember
            ? []
            : [
                {
                  key: intl.formatMessage(itemMessages.emailPreferences),
                  label: intl.formatMessage(itemMessages.emailPreferences),
                  path: SettingPath.PERSONAL_EMAIL_PREFERENCES,
                  uid: "email-preferences",
                },
                {
                  key: intl.formatMessage(itemMessages.calendarSync),
                  label: intl.formatMessage(itemMessages.calendarSync),
                  path: SettingPath.PERSONAL_CALENDAR_SYNC,
                  uid: "calendar-sync",
                },
              ]),
          ...(hasFeature(Feature.MAP)
            ? []
            : [
                {
                  key: intl.formatMessage(itemMessages.mapPreferences),
                  label: intl.formatMessage(itemMessages.mapPreferences),
                  path: SettingPath.PERSONAL_MAP_PREFERENCES,
                  uid: "map-preferences",
                },
              ]),
          {
            key: intl.formatMessage(itemMessages.profile),
            label: intl.formatMessage(itemMessages.profile),
            path: SettingPath.PERSONAL_PROFILE,
            uid: "profile",
          },
        ],
        name: intl.formatMessage(categoryMessages.personal),
        uid: "personal",
      },
      ...(isManager || isOwner
        ? [
            {
              icon: faGlobe,
              items: [
                ...(isOwner
                  ? [
                      {
                        key: intl.formatMessage(itemMessages.activityTypes),
                        label: intl.formatMessage(itemMessages.activityTypes),
                        path: SettingPath.ORGANIZATION_ACTIVITY_TYPES,
                        uid: "activity-types",
                      },
                      {
                        key: intl.formatMessage(itemMessages.organizationDetails),
                        label: intl.formatMessage(itemMessages.organizationDetails),
                        path: SettingPath.ORGANIZATION_DETAILS,
                        uid: "org-details",
                      },
                    ]
                  : []),
                {
                  key: intl.formatMessage(itemMessages.usersAndPermissions),
                  label: intl.formatMessage(itemMessages.usersAndPermissions),
                  legacyPath: "/dashboard/teams",
                  uid: "user-management",
                },
              ],
              name: intl.formatMessage(categoryMessages.organization),
              uid: "organization",
            },
          ]
        : []),
      {
        icon: faPen,
        items: [
          ...(isOwner
            ? [
                {
                  key: intl.formatMessage(itemMessages.formsCreateForms),
                  label: intl.formatMessage(itemMessages.formsCreateForms),
                  path: SettingPath.FORMS_CREATE,
                  uid: "layouts",
                },
                {
                  key: intl.formatMessage(itemMessages.formsManageFields),
                  label: intl.formatMessage(itemMessages.formsManageFields),
                  path: SettingPath.FORMS_MANAGE_FIELDS,
                  uid: "schemas",
                },
              ]
            : []),
          {
            key: intl.formatMessage(itemMessages.formsPinnedFields),
            label: intl.formatMessage(itemMessages.formsPinnedFields),
            path: SettingPath.PINNED_FIELDS,
            uid: "pinned-fields",
          },
          {
            key: intl.formatMessage(itemMessages.fileTags),
            label: intl.formatMessage(itemMessages.fileTags),
            path: SettingPath.FORMS_FILE_TAGS,
            uid: "file-tags",
          },
        ],
        name: intl.formatMessage(categoryMessages.formsAndFields),
        uid: "forms-and-fields",
      },
      ...(dataItems.length > 0
        ? [
            {
              icon: faDatabase,
              items: dataItems,
              name: intl.formatMessage(categoryMessages.data),
              uid: "data",
            },
          ]
        : []),
      {
        icon: faBell,
        items: [
          {
            key: intl.formatMessage(itemMessages.notifications),
            label: intl.formatMessage(itemMessages.notifications),
            path: SettingPath.PRIVACY_NOTIFICATIONS,
            uid: "notifications",
          },
        ],
        name: intl.formatMessage(categoryMessages.notificationAndPrivacy),
        uid: "notifications",
      },
    ];
  }, [
    doesUserHaveAccessToDupeCheck,
    doesUserHaveAccessToImports,
    hasFeature,
    intl,
    isManager,
    isOwner,
    isReadOnlyMember,
  ]);
};

export default useMenu;
